import React, { useState, useEffect } from 'react';
import axios from 'axios';
import authService from '../../services/authService';
import CustomSelect from '../../components/common/CustomSelect';
import './GradeInternship.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const GradeInternship = () => {
    const [terms, setTerms] = useState([]);
    const [selectedTermId, setSelectedTermId] = useState('');
    const [gradeFilter, setGradeFilter] = useState('ungraded');
    const [studentTypeFilter, setStudentTypeFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [studentGrades, setStudentGrades] = useState({});
    const [studentNotes, setStudentNotes] = useState({});
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    // Toast notification helper
    const showToast = (message, type = 'info') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 4000);
    };

    // Fetch terms from backend
    useEffect(() => {
        const fetchTerms = async () => {
            try {
                const response = await axios.get(`${API_URL}/terms`);
                setTerms(response.data);
                if (response.data.length > 0) {
                    setSelectedTermId(response.data[0].id);
                }
            } catch (err) {
                console.error('Error fetching terms:', err);
                setError('Failed to load terms');
            }
        };
        fetchTerms();
    }, []);

    // Fetch students when term or filters change
    useEffect(() => {
        if (!selectedTermId) return;

        const fetchStudents = async () => {
            setLoading(true);
            setError(null);
            try {
                const user = authService.getCurrentUser();
                if (!user || !user.department) {
                    setError('Kullanıcı bölümü bulunamadı.');
                    setLoading(false);
                    return;
                }

                const queryParams = new URLSearchParams();
                if (gradeFilter !== 'all') {
                    queryParams.append('gradeFilter', gradeFilter);
                }
                if (studentTypeFilter !== 'all') {
                    queryParams.append('studentTypeFilter', studentTypeFilter);
                }

                const response = await axios.get(
                    `${API_URL}/students/${user.department.id}/${selectedTermId}?${queryParams.toString()}`
                );
                setStudents(response.data);

                // Initialize grades and notes from backend data
                const grades = {};
                const notes = {};
                response.data.forEach(student => {
                    if (student.currentInternship?.grade) {
                        grades[student.currentInternship.id] = student.currentInternship.grade;
                    }
                    if (student.currentInternship?.gradeComment) {
                        notes[student.currentInternship.id] = student.currentInternship.gradeComment;
                    }
                });
                setStudentGrades(grades);
                setStudentNotes(notes);
            } catch (err) {
                console.error('Error fetching students:', err);
                setError('Failed to load students');
            } finally {
                setLoading(false);
            }
        };

        fetchStudents();
    }, [selectedTermId, gradeFilter, studentTypeFilter]);

    // Filtreleme (local search only)
    const filteredStudents = students.filter(student => {
        // İsim araması
        if (searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase();
            if (!student.name.toLowerCase().includes(query) && 
                !student.id.includes(query)) {
                return false;
            }
        }
        return true;
    });

    const handleGradeChange = (internshipId, grade) => {
        setStudentGrades(prev => ({
            ...prev,
            [internshipId]: grade
        }));
    };

    const handleNoteChange = (internshipId, note) => {
        setStudentNotes(prev => ({
            ...prev,
            [internshipId]: note
        }));
    };

    const handleSaveGrades = async () => {
        try {
            // Collect all modified grades
            const gradesToSubmit = [];
            
            filteredStudents.forEach(student => {
                const internshipId = student.currentInternship?.id;
                if (!internshipId) return;

                const grade = studentGrades[internshipId];
                const gradeComment = studentNotes[internshipId]?.trim();

                // Only submit if grade is set
                if (grade && (grade === 'S' || grade === 'U')) {
                    const gradeData = {
                        studentId: student.id,
                        internshipOrder: student.currentInternship.internshipOrder,
                        grade
                    };
                    
                    // Only add gradeComment if it has content
                    if (gradeComment) {
                        gradeData.gradeComment = gradeComment;
                    }
                    
                    gradesToSubmit.push(gradeData);
                }
            });

            if (gradesToSubmit.length === 0) {
                showToast('Kaydedilecek not bulunamadı. Lütfen en az bir öğrenciye not verin.', 'warning');
                return;
            }

            console.log('Submitting grades:', gradesToSubmit);

            // Submit bulk grades
            const response = await axios.post(
                `${API_URL}/grade-internships-bulk`,
                { grades: gradesToSubmit },
                { headers: { Authorization: `Bearer ${authService.getToken()}` } }
            );

            console.log('Grade submission response:', response.data);

            showToast(`${response.data.successCount} öğrencinin notu başarıyla kaydedildi!`, 'success');
            
            // Refresh the student list
            const user = authService.getCurrentUser();
            const queryParams = new URLSearchParams();
            if (gradeFilter !== 'all') {
                queryParams.append('gradeFilter', gradeFilter);
            }
            if (studentTypeFilter !== 'all') {
                queryParams.append('studentTypeFilter', studentTypeFilter);
            }
            const refreshResponse = await axios.get(
                `${API_URL}/students/${user.department.id}/${selectedTermId}?${queryParams.toString()}`
            );
            setStudents(refreshResponse.data);
        } catch (err) {
            console.error('Full error object:', err);
            console.error('Error response:', err.response);
            console.error('Error message:', err.message);
            
            let errorMessage = 'Notları kaydederken bir hata oluştu.';
            
            if (err.response) {
                // Server responded with error
                errorMessage = err.response.data?.error || err.response.data?.message || `Server error: ${err.response.status}`;
                console.error('Server error data:', err.response.data);
            } else if (err.request) {
                // Request made but no response
                errorMessage = 'Sunucuya ulaşılamadı. Lütfen backend sunucusunun çalıştığından emin olun.';
                console.error('No response received:', err.request);
            } else {
                // Error in request setup
                errorMessage = err.message;
            }
            
            showToast(`Hata: ${errorMessage}`, 'error');
        }
    };

    const getGradeValue = (student) => {
        const internshipId = student.currentInternship?.id;
        if (!internshipId) return '';
        
        // Check local state first
        if (studentGrades[internshipId] !== undefined) {
            return studentGrades[internshipId];
        }
        return student.currentInternship.grade || '';
    };

    const getNoteValue = (student) => {
        const internshipId = student.currentInternship?.id;
        if (!internshipId) return '';
        
        // Check local state first
        if (studentNotes[internshipId] !== undefined) {
            return studentNotes[internshipId];
        }
        return student.currentInternship.gradeComment || '';
    };



    // Prepare filter options
    const termOptions = terms.map(term => ({ value: term.id, label: term.name }));
    const gradeOptions = [
        { value: 'all', label: 'Tümü' },
        { value: 'S', label: 'S (Başarılı)' },
        { value: 'U', label: 'U (Başarısız)' },
        { value: 'ungraded', label: 'Puanlanmamış' }
    ];
    const typeOptions = [
        { value: 'all', label: 'Tümü' },
        { value: 'first', label: 'Zorunlu Staj 1' },
        { value: 'second', label: 'Zorunlu Staj 2' }
    ];

    const gradingOptions = [
        { value: '', label: 'Seçiniz' },
        { value: 'S', label: 'S (Başarılı)' },
        { value: 'U', label: 'U (Başarısız)' }
    ];

    const renderGradeLabel = (option) => {
        if (option.value === 'S') return <span style={{ color: '#28a745', fontWeight: '600' }}>{option.label}</span>;
        if (option.value === 'U') return <span style={{ color: '#dc3545', fontWeight: '600' }}>{option.label}</span>;
        return option.label;
    };

    return (
        <div>
            <h2>Staj Puanlandır</h2>
            <p>Öğrencilerin staj çalışmalarını değerlendirin ve notlandırın.</p>

            {error && <div className="error-message">{error}</div>}

            {/* Filtreler ve Arama */}
            <div className="filters-card">
                <div className="filter-row">
                    <div className="filter-group">
                        <label>Dönem Seçiniz:</label>
                        <CustomSelect
                            options={termOptions}
                            value={selectedTermId}
                            onChange={(val) => setSelectedTermId(val)}
                            disabled={loading}
                            placeholder="Dönem Seçiniz"
                        />
                    </div>

                    <div className="filter-group">
                        <label>Puana Göre Filtrele:</label>
                        <CustomSelect
                            options={gradeOptions}
                            value={gradeFilter}
                            onChange={(val) => setGradeFilter(val)}
                            disabled={loading}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Staj Tipi:</label>
                        <CustomSelect
                            options={typeOptions}
                            value={studentTypeFilter}
                            onChange={(val) => setStudentTypeFilter(val)}
                            disabled={loading}
                        />
                    </div>
                </div>

                {/* Arama Çubuğu */}
                <div className="search-bar">
                    <i className="fa-solid fa-search"></i>
                    <input 
                        type="text"
                        placeholder="Öğrenci adı veya numarası ile ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={loading}
                    />
                    {searchQuery && (
                        <button className="clear-search" onClick={() => setSearchQuery('')}>
                            <i className="fa-solid fa-times"></i>
                        </button>
                    )}
                </div>
            </div>

            {/* Puanlama Tablosu */}
            <div className="table-container-card">
                <div className="table-header-info">
                    <h3>{terms.find(t => t.id === selectedTermId)?.name || 'Dönem'} Staj Puanlama</h3>
                    <span className="student-count">{filteredStudents.length} öğrenci</span>
                </div>


                {loading ? (
                    <div style={{ textAlign: 'center', padding: '30px' }}>Yükleniyor...</div>
                ) : (
                    <div className="table-wrapper">
                        <table className="styled-table grading-table">
                            <thead>
                                <tr>
                                    <th style={{width: '10%'}}>Numara</th>
                                    <th style={{width: '15%'}}>Ad Soyad</th>
                                    <th style={{width: '15%'}}>Staj Yeri</th>
                                        <th style={{width: '10%'}}>Başlangıç</th>
                                    <th style={{width: '10%'}}>Bitiş</th>
                                    <th style={{width: '12%'}}>Staj Notu</th>
                                    <th style={{width: '28%'}}>Açıklama (Opsiyonel)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudents.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '30px' }}>
                                            <i className="fa-solid fa-circle-info"></i> Seçili filtrelere uygun öğrenci bulunamadı.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredStudents.map(student => {
                                        const internship = student.currentInternship;
                                        if (!internship) return null;
                                        
                                        return (
                                            <tr key={`${student.id}-${internship.id}`}>
                                                <td>{student.id}</td>
                                                <td>{student.name}</td>
                                                <td>{internship.company || 'N/A'}</td>
                                                <td>{internship.startDate ? new Date(internship.startDate).toLocaleDateString('tr-TR') : 'N/A'}</td>
                                                <td>{internship.endDate ? new Date(internship.endDate).toLocaleDateString('tr-TR') : 'N/A'}</td>
                                                <td style={{overflow: 'visible'}}>
                                                    <CustomSelect
                                                        className="table-select"
                                                        options={gradingOptions}
                                                        value={getGradeValue(student)}
                                                        onChange={(val) => handleGradeChange(internship.id, val)}
                                                        renderLabel={renderGradeLabel}
                                                        placeholder="Seçiniz"
                                                    />
                                                </td>
                                                <td>
                                                    <input 
                                                        type="text"
                                                        className="note-input"
                                                        placeholder="Açıklama ekleyin..."
                                                        value={getNoteValue(student)}
                                                        onChange={(e) => handleNoteChange(internship.id, e.target.value)}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
                
                <div className="table-footer">
                    <button 
                        className="btn-save-grades" 
                        onClick={handleSaveGrades}
                        disabled={loading}
                    >
                        <i className="fa-solid fa-save"></i> 
                        Tüm Notları Kaydet
                    </button>
                </div>
            </div>

            {/* Toast Notification */}
            {toast.show && (
                <div className={`toast-notification toast-${toast.type}`}>
                    <div className="toast-content">
                        <i className={`fa-solid fa-${
                            toast.type === 'success' ? 'check-circle' :
                            toast.type === 'error' ? 'exclamation-circle' :
                            toast.type === 'warning' ? 'exclamation-triangle' :
                            'info-circle'
                        }`}></i>
                        <span>{toast.message}</span>
                    </div>
                    <button className="toast-close" onClick={() => setToast({ show: false, message: '', type: '' })}>
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>
            )}
        </div>
    );
};

export default GradeInternship;
